"use client"

import Image from "next/image"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@ui/shad"
import { ExternalLink } from "lucide-react"

type CrmExportSource = "wealthbox" | "redtail"

interface CrmExportInstructionsProps {
  sourceType: CrmExportSource
  className?: string
}

export function CrmExportInstructions({ sourceType, className }: CrmExportInstructionsProps) {
  if (sourceType === "wealthbox") {
    return (
      <div className={className}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h4 className="text-sm font-medium">How to export from Wealthbox</h4>
          <a
            href="https://app.crmworkspace.com/settings/data_backup"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/80 flex items-center gap-1 text-sm"
          >
            Open Wealthbox export page
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <ol className="text-muted-foreground ml-4 list-decimal space-y-2 text-sm">
          <li>
            Go to the Wealthbox export settings (
            <a
              href="https://app.crmworkspace.com/settings/data_backup"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link hover:text-link-hover font-semibold underline underline-offset-2"
            >
              click here
            </a>
            )
          </li>
          <li>
            Click the green <strong>Request Export</strong> button (
            <HoverCard>
              <HoverCardTrigger asChild>
                <button type="button" className="underline underline-offset-2">
                  screenshot
                </button>
              </HoverCardTrigger>
              <HoverCardContent className="w-auto p-1" side="right">
                <Image
                  src="/wealthbox/request_export_screenshot.png"
                  alt="Wealthbox request export button"
                  width={720}
                  height={480}
                  className="rounded"
                />
              </HoverCardContent>
            </HoverCard>
            )
          </li>
          <li>Wait 1-2 minutes for the export to complete. You may need to reload the page.</li>
          <li>
            Download the file to your computer (
            <HoverCard>
              <HoverCardTrigger asChild>
                <button type="button" className="underline underline-offset-2">
                  screenshot
                </button>
              </HoverCardTrigger>
              <HoverCardContent className="w-auto p-1" side="right">
                <Image
                  src="/wealthbox/download_export_screenshot.png"
                  alt="Wealthbox download export"
                  width={720}
                  height={480}
                  className="rounded"
                />
              </HoverCardContent>
            </HoverCard>
            )
          </li>
        </ol>
      </div>
    )
  }

  return (
    <div className={className}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h4 className="text-sm font-medium">How to export from Redtail</h4>
        <a
          href="https://crm.redtailtechnology.com/admin/lists/backups"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 flex items-center gap-1 text-sm"
        >
          Open Redtail backups page
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      <ol className="text-muted-foreground ml-4 list-decimal space-y-2 text-sm">
        <li>
          Go to the Redtail database backups page (
          <a
            href="https://crm.redtailtechnology.com/admin/lists/backups"
            target="_blank"
            rel="noopener noreferrer"
            className="text-link hover:text-link-hover font-semibold underline underline-offset-2"
          >
            click here
          </a>
          )
        </li>
        <li>
          Click the <strong>Request Database Backup</strong> button (
          <HoverCard>
            <HoverCardTrigger asChild>
              <button type="button" className="underline underline-offset-2">
                screenshot
              </button>
            </HoverCardTrigger>
            <HoverCardContent className="w-auto p-1" side="right">
              <Image
                src="/redtail/request_backup_screenshot.png"
                alt="Redtail request backup button"
                width={720}
                height={480}
                className="rounded"
              />
            </HoverCardContent>
          </HoverCard>
          )
        </li>
        <li>Wait 1-2 minutes for the backup to generate. You may need to reload the page.</li>
        <li>Click the file name to download the file.</li>
      </ol>
    </div>
  )
}
